import {
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  HStack,
  Flex,
  SimpleGrid,
} from '@chakra-ui/react'
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons'
import React, { useState } from 'react'
import { AgentContact } from '../../api/modules/agentApi'
import { useAgent } from '../../hooks/agents/useAgent'

interface AgentContactsManagerProps {
  agentId: string
}

export const AgentContactsManager: React.FC<AgentContactsManagerProps> = ({ agentId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const [editingContact, setEditingContact] = useState<AgentContact | null>(null)
  const [formData, setFormData] = useState<AgentContact>({
    country: '',
    name: '',
    phone: '',
    email: '',
  })

  const {
    contactsData,
    isLoadingContacts,
    createContact,
    updateContact,
    deleteContact,
    isCreatingContact,
    isUpdatingContact,
  } = useAgent(agentId)

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.700', 'white')
  const subtleColor = useColorModeValue('gray.500', 'gray.400')
  const contactCardBg = useColorModeValue('gray.50', 'gray.700')

  const handleOpenCreateModal = () => {
    setEditingContact(null)
    setFormData({
      country: '',
      name: '',
      phone: '',
      email: '',
    })
    onOpen()
  }

  const handleOpenEditModal = (contact: AgentContact) => {
    setEditingContact(contact)
    setFormData(contact)
    onOpen()
  }

  const handleCloseModal = () => {
    setEditingContact(null)
    setFormData({
      country: '',
      name: '',
      phone: '',
      email: '',
    })
    onClose()
  }

  const handleSubmit = () => {
    if (editingContact) {
      updateContact({ country: editingContact.country, contact: formData })
        .then(() => {
          toast({
            title: 'Contact updated',
            description: 'The contact has been updated successfully.',
            status: 'success',
            duration: 3000,
          })
          handleCloseModal()
        })
        .catch(() => {
          toast({
            title: 'Error updating contact',
            description: 'Failed to update contact',
            status: 'error',
            duration: 5000,
          })
        })
    } else {
      createContact(formData)
        .then(() => {
          toast({
            title: 'Contact created',
            description: 'The contact has been created successfully.',
            status: 'success',
            duration: 3000,
          })
          handleCloseModal()
        })
        .catch(() => {
          toast({
            title: 'Error creating contact',
            description: 'Failed to create contact',
            status: 'error',
            duration: 5000,
          })
        })
    }
  }

  const handleDelete = (country: string) => {
    if (window.confirm(`Are you sure you want to delete the contact for ${country}?`)) {
      deleteContact(country)
        .then(() => {
          toast({
            title: 'Contact deleted',
            description: 'The contact has been deleted successfully.',
            status: 'success',
            duration: 3000,
          })
        })
        .catch(() => {
          toast({
            title: 'Error deleting contact',
            description: 'Failed to delete contact',
            status: 'error',
            duration: 5000,
          })
        })
    }
  }

  return (
    <VStack
      spacing={4}
      align='stretch'
      bg={cardBg}
      p={6}
      borderRadius='xl'
      borderWidth='1px'
      borderColor={borderColor}
    >
      <Flex justify='space-between' align='center' wrap='wrap' gap={4}>
        <VStack align='start' spacing={1}>
          <Heading size='md' color={textColor}>
            Agent Contacts
          </Heading>
          <Text fontSize='sm' color={subtleColor}>
            Manage contact information for different countries
          </Text>
        </VStack>
        <Button
          leftIcon={<AddIcon />}
          colorScheme='primaryScheme'
          size='sm'
          onClick={handleOpenCreateModal}
          whiteSpace='nowrap'
        >
          Add Contact
        </Button>
      </Flex>

      {isLoadingContacts ? (
        <Text textAlign='center' py={8} color={subtleColor}>
          Loading contacts...
        </Text>
      ) : contactsData?.contacts && contactsData.contacts.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={4}>
          {contactsData.contacts.map((contact: AgentContact) => (
            <Card
              key={contact.country}
              bg={contactCardBg}
              borderColor={borderColor}
              borderWidth='1px'
              _hover={{ shadow: 'md' }}
              transition='all 0.2s'
            >
              <CardBody p={4}>
                <VStack align='start' spacing={3} h='full'>
                  <Flex justify='space-between' w='full' align='start'>
                    <VStack align='start' spacing={0} flex='1'>
                      <Heading size='sm' color={textColor}>
                        {contact.country}
                      </Heading>
                      <Text fontSize='xs' color={subtleColor}>
                        Contact
                      </Text>
                    </VStack>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label='Edit contact'
                        icon={<EditIcon />}
                        size='xs'
                        variant='ghost'
                        onClick={() => handleOpenEditModal(contact)}
                        colorScheme='blue'
                      />
                      <IconButton
                        aria-label='Delete contact'
                        icon={<DeleteIcon />}
                        size='xs'
                        variant='ghost'
                        colorScheme='red'
                        onClick={() => handleDelete(contact.country)}
                      />
                    </HStack>
                  </Flex>

                  <Divider />

                  <VStack align='start' spacing={2} w='full' fontSize='sm'>
                    <Box>
                      <Text fontWeight='500' color={textColor}>
                        {contact.name}
                      </Text>
                    </Box>

                    <Stack spacing={1} w='full'>
                      <Flex align='center' gap={2} minW={0}>
                        <Text fontWeight='500' color={subtleColor} minW='60px'>
                          Phone:
                        </Text>
                        <Text color={textColor} isTruncated title={contact.phone}>
                          {contact.phone || '-'}
                        </Text>
                      </Flex>

                      <Flex align='center' gap={2} minW={0}>
                        <Text fontWeight='500' color={subtleColor} minW='60px'>
                          Email:
                        </Text>
                        <Text
                          as='a'
                          href={`mailto:${contact.email}`}
                          color='blue.500'
                          _hover={{ textDecoration: 'underline' }}
                          isTruncated
                          title={contact.email}
                        >
                          {contact.email}
                        </Text>
                      </Flex>
                    </Stack>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      ) : (
        <Box textAlign='center' py={12}>
          <Text color={subtleColor} mb={4}>
            No contacts configured yet
          </Text>
          <Button leftIcon={<AddIcon />} colorScheme='primaryScheme' size='sm' onClick={handleOpenCreateModal}>
            Create First Contact
          </Button>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 4, md: 0 }}>
          <ModalHeader>{editingContact ? 'Edit Contact' : 'Add Contact'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize='sm'>Country</FormLabel>
                <Input
                  size='sm'
                  value={formData.country}
                  onChange={e => setFormData({ ...formData, country: e.target.value })}
                  placeholder='e.g., Netherlands, Belgium'
                  isDisabled={!!editingContact}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm'>Name</FormLabel>
                <Input
                  size='sm'
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder='John Doe'
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm'>Phone</FormLabel>
                <Input
                  size='sm'
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder='+31610317444'
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize='sm'>Email</FormLabel>
                <Input
                  size='sm'
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder='john@example.com'
                  type='email'
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button variant='ghost' size='sm' onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              colorScheme='primaryScheme'
              size='sm'
              onClick={handleSubmit}
              isLoading={isCreatingContact || isUpdatingContact}
              isDisabled={!formData.country || !formData.name || !formData.phone || !formData.email}
            >
              {editingContact ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
